import type { ReactElement } from 'react';
import React from 'react';

import { AutoTagIcon } from '@vlabs/icons';
import { Control, Tag } from '@vlabs/uikit';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './ClassifyDeepfakeButton.module.sass';
import type { TFrame } from '../model/types';
import { useClassifyDeepfake } from '../model/useClassifyDeepfake';

interface TClassifyDeepfakeButtonProps {
  frames: TFrame[];
  disabled?: boolean;
  ttl?: number;
  className?: string;
}

const typeLabelMap: Record<string, string> = {
  face_swap: 'Face Swap',
  reenactment: 'Reenactment',
  face_synt: 'Face Synthesis',
  research: 'Research',
};

// FIXME после GUI-3180 вынести переводы в features/classify-deepfake/i18n.ts
export function ClassifyDeepfakeButton({
  frames,
  disabled = false,
  ttl = 7,
  className = undefined,
}: TClassifyDeepfakeButtonProps): ReactElement {
  const { t } = useTranslation();

  const {
    status,
    result,
    error,
    canClassify,
    runClassification,
  } = useClassifyDeepfake({
    frames,
    enabled: !disabled,
    ttl,
  });

  const errorMessage = error as { detail?: string; message?: string };

  return (
    <div className={cn(st.Root, className)}>
      {!result && (
        <Control.Button
          className={st.Button}
          disabled={!canClassify}
          isLoading={status === 'loading'}
          kind="secondary"
          onClick={runClassification}
          size="small"
          variant="outlined"
        >
          <div className={st.ButtonContent}>
            {t('monokit:classify deepfake.button')}
            <Tag
              className={cn(st.BetaTag, !canClassify && st.BetaTag_disabled)}
              icon={<AutoTagIcon />}
              shape="square"
              value="Beta"
            />
          </div>
        </Control.Button>
      )}

      {status === 'success' && !!result && (
      <React.Fragment>
        <div className={st.Row}>
          <div className={st.Label}>{t('monokit:classify deepfake.results.type')}</div>
          <div className={st.Value}>
            {typeLabelMap[result.type] ?? result.type}
          </div>
        </div>

        <div className={st.Row}>
          <div className={st.Label}>{t('monokit:classify deepfake.results.instrument')}</div>
          <div className={st.Value}>
            {result.instrument}
          </div>
        </div>
      </React.Fragment>
      )}

      {status === 'error' && error ? (
        <div className={st.Error}>
          {t('monokit:classify deepfake.error')}
          :
          {' '}
          {errorMessage?.detail ?? errorMessage?.message ?? String(error)}
        </div>
      ) : null}
    </div>
  );
}
